import * as React from 'react';

import { translate } from '~l10n/client';
import { SmallButton } from '~shared/components/buttons';
import { Spinner } from '~shared/components/Spinner';

interface AIQuestionsLoadingSpinnerProps {
  goBack: () => void;
  isFileUpload?: boolean;
}

type ProgressStage = 1 | 2 | 3 | 4;

const STAGE_TIMINGS = {
  STAGE_1_DURATION: 3000,
  STAGE_2_DURATION: 10000,
  STAGE_3_DURATION: 40000,
  LONG_RUNNING_THRESHOLD: 90000,
};

function getStageMessage(stage: ProgressStage, isFileUpload: boolean): string {
  switch (stage) {
    case 1:
      return isFileUpload
        ? translate('Reading your file…')
        : translate('Reading your prompt…');
    case 2:
      return translate('Identifying questions and key concepts…');
    case 3:
      return translate('Generating quiz questions…');
    case 4:
      return translate('Finalizing and formatting questions…');
    default:
      return translate('Processing…');
  }
}

export function AIQuestionsLoadingSpinner({
  goBack,
  isFileUpload = false,
}: AIQuestionsLoadingSpinnerProps) {
  const [stage, setStage] = React.useState<ProgressStage>(1);
  const [showLongRunningMessage, setShowLongRunningMessage] =
    React.useState(false);
  const startTimeRef = React.useRef(Date.now());

  React.useEffect(() => {
    startTimeRef.current = Date.now();

    const stage1Timer = setTimeout(() => {
      setStage(2);
    }, STAGE_TIMINGS.STAGE_1_DURATION);

    const stage2Timer = setTimeout(() => {
      setStage(3);
    }, STAGE_TIMINGS.STAGE_1_DURATION + STAGE_TIMINGS.STAGE_2_DURATION);

    const stage3Timer = setTimeout(() => {
      setStage(4);
    }, STAGE_TIMINGS.STAGE_1_DURATION + STAGE_TIMINGS.STAGE_2_DURATION + STAGE_TIMINGS.STAGE_3_DURATION);

    const longRunningTimer = isFileUpload
      ? setTimeout(() => {
          setShowLongRunningMessage(true);
        }, STAGE_TIMINGS.LONG_RUNNING_THRESHOLD)
      : undefined;

    return () => {
      clearTimeout(stage1Timer);
      clearTimeout(stage2Timer);
      clearTimeout(stage3Timer);
      if (longRunningTimer) {
        clearTimeout(longRunningTimer);
      }
    };
  }, [isFileUpload]);

  const stageMessage = getStageMessage(stage, isFileUpload);

  return (
    <div className="grid items-center justify-center h-full shadow-border-t">
      <div className="flex flex-col items-center gap-8">
        <Spinner size="medium" />
        <div className="flex flex-col items-center gap-6">
          <p className="text-display font-display text-blue-900 font-500 text-md leading-10">
            {stageMessage}
          </p>
          {showLongRunningMessage && (
            <p className="font-sans text-blue-700 text-sm leading-8 max-w-md text-center">
              {translate(
                'This is taking a bit longer than usual. Thanks for your patience.'
              )}
            </p>
          )}
          <SmallButton appearance="default" className="my-1" onClick={goBack}>
            {translate('Cancel')}
          </SmallButton>
        </div>
      </div>
    </div>
  );
}
